import React from "react";
import PropTypes from "prop-types";
import { Building2 } from "lucide-react";

import { UICard, UIProgressBar } from "../../ui";
import DashboardEmptyState from "../DashboardEmptyState";
import { STATUS_COLOR } from "./constants";
import "../dashboardSection.scss";
import "./CompaniesByStatus.scss";

const formatCount = (value) => Number(value).toLocaleString("en-US");
const formatPercent = (value) => Number(value).toFixed(1);

/**
 * Admin dashboard "Companies by Status" — the total registered companies plus a
 * proportional bar per status (Active / Suspended), or an empty state when no
 * companies exist yet. Every company is counted in exactly one status, so the
 * percentages are each status's share of the total.
 */
const CompaniesByStatus = ({
  title,
  totalLabel,
  total,
  statuses,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
}) => {
  const hasData = Number(total) > 0;

  return (
    <UICard title={title} className="dashboard-card companies-by-status">
      {hasData ? (
        <div className="companies-by-status__content">
          <p className="companies-by-status__total">
            <span className="companies-by-status__total-value">
              {formatCount(total)}
            </span>
            <span className="companies-by-status__total-label">
              {totalLabel}
            </span>
          </p>

          <ul className="companies-by-status__list">
            {statuses.map((status) => {
              const color = STATUS_COLOR[status.id] || STATUS_COLOR.active;
              const percent = total ? (status.count / total) * 100 : 0;

              return (
                <li key={status.id} className="companies-by-status__item">
                  <div className="companies-by-status__item-head">
                    <span className="companies-by-status__legend">
                      <span
                        className="companies-by-status__dot"
                        style={{ background: color, borderColor: color }}
                      />
                      {status.label}
                    </span>
                    <span className="companies-by-status__figures">
                      <span className="companies-by-status__count">
                        {formatCount(status.count)}
                      </span>
                      <span className="companies-by-status__percent">
                        {formatPercent(percent)}
                        <span className="companies-by-status__percent-suffix">
                          %
                        </span>
                      </span>
                    </span>
                  </div>
                  <UIProgressBar
                    value={Math.max(0, Math.min(100, percent))}
                    barColor={color}
                    className="companies-by-status__bar"
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <DashboardEmptyState
          icon={<Building2 />}
          title={emptyTitle}
          description={emptyDescription}
          actionLabel={emptyActionLabel}
          onAction={onEmptyAction}
        />
      )}
    </UICard>
  );
};

CompaniesByStatus.propTypes = {
  title: PropTypes.string.isRequired,
  totalLabel: PropTypes.string.isRequired,
  total: PropTypes.number,
  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ),
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
  emptyActionLabel: PropTypes.string,
  onEmptyAction: PropTypes.func,
};

CompaniesByStatus.defaultProps = {
  total: 0,
  statuses: [],
  emptyActionLabel: "",
  onEmptyAction: undefined,
};

export default CompaniesByStatus;
